import { useCallback, useEffect, useState } from 'react';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

import { useAWSAuth } from '../contexts/AWSAuthContext';
import type { ScanRecord } from '../services/scanService';
import { listUserScans } from '../services/scanService';

interface LatestScanState {
  latestScan: ScanRecord | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const selectLatestCompleted = (items: ScanRecord[]): ScanRecord | null => {
  if (!items.length) {
    return null;
  }

  const completed = items.find((item) => item.status === 'complete');
  return completed ?? items[0];
};

export const useLatestScan = (): LatestScanState => {
  const { getCurrentSession } = useAWSAuth();
  const [latestScan, setLatestScan] = useState<ScanRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLatest = useCallback(async () => {
    let session: CognitoUserSession | null = null;
    setLoading(true);
    setError(null);

    try {
      session = await getCurrentSession();
      if (!session) {
        setLatestScan(null);
        setLoading(false);
        return;
      }

      const uid = session.getIdToken().payload.sub;
      const { items } = await listUserScans(session, uid, 5);

      setLatestScan(selectLatestCompleted(items));
    } catch (err) {
      console.error('Failed to load latest scan', err);
      setError(
        err instanceof Error ? err.message : 'Unable to load your latest scan right now.'
      );
    } finally {
      setLoading(false);
    }
  }, [getCurrentSession]);

  useEffect(() => {
    void fetchLatest();

    const handler = () => {
      void fetchLatest();
    };

    window.addEventListener('glowlens:scanReady', handler);
    window.addEventListener('glowlens:scanDeleted', handler);
    return () => {
      window.removeEventListener('glowlens:scanReady', handler);
      window.removeEventListener('glowlens:scanDeleted', handler);
    };
  }, [fetchLatest]);

  return {
    latestScan,
    loading,
    error,
    refresh: fetchLatest,
  };
};

export default useLatestScan;

